import { Fragment, ReactNode, useMemo, useState } from 'react';
import { FaChevronRight, FaFolder } from 'react-icons/fa6';
import { buildFolderTree, FolderNode, sortedChildren } from './folderTree.utils';
import { FolderAccordion, FolderAccordionHeader, FolderBody, FolderCountPill } from './FolderList.styles';

type FolderListNodeProps<T> = {
  node: FolderNode<T>;
  forceExpand: boolean;
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
};

const FolderListNode = <T,>({ node, forceExpand, getKey, renderItem }: FolderListNodeProps<T>) => {
  const [open, setOpen] = useState(false);
  const expanded = forceExpand || open;
  const childFolders = useMemo(() => sortedChildren(node), [node]);

  return (
    <FolderAccordion>
      <FolderAccordionHeader type="button" onClick={() => setOpen((prev) => !prev)} aria-expanded={expanded}>
        <span className="folder-chevron" style={{ transform: `rotate(${expanded ? 90 : 0}deg)` }}>
          <FaChevronRight size={12} />
        </span>
        <span className="folder-icon">
          <FaFolder size={15} />
        </span>
        <span className="folder-name">{node.name}</span>
        <FolderCountPill>{node.count}</FolderCountPill>
      </FolderAccordionHeader>
      {expanded && (
        <FolderBody>
          {childFolders.map((child) => (
            <FolderListNode
              key={child.path}
              node={child}
              forceExpand={forceExpand}
              getKey={getKey}
              renderItem={renderItem}
            />
          ))}
          {node.items.map((item) => (
            <Fragment key={getKey(item)}>{renderItem(item)}</Fragment>
          ))}
        </FolderBody>
      )}
    </FolderAccordion>
  );
};

type FolderListProps<T> = {
  items: T[];
  getKey: (item: T) => string;
  getFolderPath: (item: T) => string | null | undefined;
  /** Should return a compact list row. */
  renderItem: (item: T) => ReactNode;
  forceExpand?: boolean;
};

const FolderList = <T,>({ items, getKey, getFolderPath, renderItem, forceExpand = false }: FolderListProps<T>) => {
  const root = useMemo(() => buildFolderTree(items, getFolderPath), [items, getFolderPath]);
  const rootFolders = useMemo(() => sortedChildren(root), [root]);

  return (
    <div className="flex flex-col gap-2.5">
      {rootFolders.map((folder) => (
        <FolderListNode
          key={folder.path}
          node={folder}
          forceExpand={forceExpand}
          getKey={getKey}
          renderItem={renderItem}
        />
      ))}
      {root.items.map((item) => (
        <Fragment key={getKey(item)}>{renderItem(item)}</Fragment>
      ))}
    </div>
  );
};

export default FolderList;
